import { Children, ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import PopularItems from '../../modules/PopularItems';
import InfiniteScroll from '@/presentation/components/modules/InfiniteScroll';
import { Address } from 'viem';
import { MutationStatus } from '@tanstack/react-query';
import ConnectButton from '@/presentation/components/widgets/ConnectButton';

export interface AllProps {
  token: Address | '';
  popularTokens: ReactNode;
  popularCollections: ReactNode;
  collections: ReactNode;
  tokens: ReactNode;
  hasMoreTokens: boolean;
  hasMoreCollections: boolean;
  onMoreClick: () => void;
  status: MutationStatus;
  isConnected: boolean;
}

export const All = ({
  popularCollections,
  popularTokens,
  hasMoreTokens,
  hasMoreCollections,
  onMoreClick,
  collections,
  tokens,
  status,
  isConnected,
}: AllProps) => {
  const t = useTranslations('Swap');
  return (
    <>
      <header className="px-4 pt-4">
        <h3 className="text-gray-400 font-semibold text-xs uppercase mb-2">
          {t('Search.popularNfts')}
        </h3>
        <div className="flex items-center flex-wrap mb-2">
          {popularCollections}
        </div>
        <h3 className="text-gray-400 font-semibold text-xs uppercase mb-2">
          {t('Search.popularTokens')}
        </h3>
        <PopularItems items={popularTokens} />
      </header>
      <section className="">
        <h3 className="text-gray-400 font-semibold text-xs mx-4 my-2">
          {t('Tabs.nfts')}
        </h3>
        <ul className="">
          <InfiniteScroll
            key="all-collections-infinite-scroll"
            loaderElement="li"
            scrollable={hasMoreCollections}
            onLoadMore={onMoreClick}
          >
            {status === 'loading' ? (
              new Array(5).fill(null).map((x, i) => (
                <div
                  className="w-full flex items-center px-4 py-2 animate animate-pulse"
                  key={i}
                >
                  <div className="w-8 h-8 flex-none mr-2 rounded-full bg-gray-300/50" />
                  <div className="h-6 w-1/4 bg-gray-300/50 rounded-sm" />
                  <div className="ml-auto h-4 w-1/3 bg-gray-300/50 rounded-sm" />
                </div>
              ))
            ) : Children.count(collections) ? (
              collections
            ) : isConnected ? (
              <div className="px-4 py-8 text-center text-gray-500">
                {'Nothing to see here :('}
              </div>
            ) : (
              <div className="flex flex-col items-center p-4 justify-center">
                <p className="mb-6">
                  {'Connect your wallet to view your NFTs'}
                </p>
                <ConnectButton />
              </div>
            )}
          </InfiniteScroll>
        </ul>
        <h3 className="text-gray-400 font-semibold text-xs uppercase mx-4 my-2">
          {t('Tabs.tokens')}
        </h3>
        <ul className="">
          <InfiniteScroll
            key="all-tokens-infinite-scroll"
            loaderElement="li"
            scrollable={hasMoreTokens}
            onLoadMore={onMoreClick}
          >
            {status === 'loading' ? (
              new Array(5).fill(null).map((x, i) => (
                <div
                  className="w-full flex items-center px-4 py-2 animate animate-pulse"
                  key={i}
                >
                  <div className="w-8 h-8 flex-none mr-2 rounded-full bg-gray-300/50" />
                  <div className="h-6 w-1/4 bg-gray-300/50 rounded-sm" />
                  <div className="ml-auto h-4 w-1/3 bg-gray-300/50 rounded-sm" />
                </div>
              ))
            ) : Children.count(tokens) ? (
              tokens
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                {'Nothing to see here :('}
              </div>
            )}
          </InfiniteScroll>
        </ul>
      </section>
    </>
  );
};

export default All;
